/**
 * 员工校区授权对话框（staff-rbac-wechat-login:SC-44）。
 *
 * 对齐原型 StaffGrantModal：校区 + 角色（多选）+ 有效期（生效/失效），
 * 提交 PUT /staffs/:id/grants（整体覆盖授权列表）。
 * 校区与角色下拉数据由父组件加载后传入（避免弹窗内部重复请求）。
 */
import { useEffect, useState } from 'react';
import { Button, DatePicker, Select, Space, Typography } from 'antd';
import { message } from '../../../utils/antd-message';
import { DraggableModal } from '../../../components/DraggableModal';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { staffApi, type StaffCampusGrant, type StaffDetail } from '../../../api/staff.api';
import type { RoleDetail } from '../../../api/role.api';
import type { CampusItem } from '../../../api/campuses.api';
import { ApiError } from '../../../api/transport';

const { Text } = Typography;

/** 编辑态的授权行（日期为 Dayjs 便于 DatePicker 接入）。 */
interface GrantRow {
  campusId?: string;
  roleIds: string[];
  validFrom?: Dayjs;
  validTo?: Dayjs | null;
}

export interface StaffGrantModalProps {
  /** 员工详情（含现有 campusGrants 作为初始值） */
  staff: StaffDetail;
  /** 校区列表（供下拉选择） */
  campuses: CampusItem[];
  /** 角色列表（仅启用角色，供下拉选择） */
  roles: RoleDetail[];
  /** 关闭回调 */
  onClose: () => void;
  /** 保存成功回调（供父组件刷新） */
  onSaved?: () => void;
}

export function StaffGrantModal({ staff, campuses, roles, onClose, onSaved }: StaffGrantModalProps) {
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // 初始化：从员工现有授权填充
  useEffect(() => {
    const initial: GrantRow[] = (staff.campusGrants ?? []).map((g) => ({
      campusId: g.campusId,
      roleIds: g.roleIds,
      validFrom: g.validFrom ? dayjs(g.validFrom) : undefined,
      validTo: g.validTo ? dayjs(g.validTo) : null,
    }));
    setGrants(initial.length > 0 ? initial : [{ roleIds: [], validFrom: dayjs(), validTo: null }]);
  }, [staff]);

  const campusOptions = campuses.map((c) => ({ label: c.name, value: c.id }));
  const roleOptions = roles
    .filter((r) => r.status === 'ACTIVE')
    .map((r) => ({ label: r.name, value: r.id }));

  function updateGrant(index: number, patch: Partial<GrantRow>) {
    setGrants((prev) => prev.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  }

  async function submit() {
    if (grants.some((g) => !g.campusId)) {
      message.warning('请为每行授权选择校区');
      return;
    }
    setSubmitting(true);
    try {
      // 转换为后端 DTO：日期转 ISO 字符串，失效日期空表示长期有效
      const payload: StaffCampusGrant[] = grants.map((g) => ({
        campusId: g.campusId!,
        roleIds: g.roleIds,
        validFrom: g.validFrom ? g.validFrom.toISOString() : new Date().toISOString(),
        validTo: g.validTo ? g.validTo.toISOString() : undefined,
      }));
      await staffApi.saveStaffGrants(staff.id, { grants: payload });
      message.success('已保存员工授权');
      onSaved?.();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) message.error(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DraggableModal
      open
      title={`员工授权 · ${staff.name}`}
      onCancel={onClose}
      destroyOnHidden
      width={720}
      onOk={() => void submit()}
      confirmLoading={submitting}
      okText="保存"
      cancelText="取消"
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {grants.map((grant, index) => (
          <Space key={index} wrap>
            <Select
              style={{ width: 150 }}
              placeholder="校区"
              value={grant.campusId}
              options={campusOptions}
              onChange={(v) => updateGrant(index, { campusId: v })}
            />
            <Select
              mode="multiple"
              style={{ minWidth: 200 }}
              placeholder="角色（可多选）"
              value={grant.roleIds}
              options={roleOptions}
              onChange={(v) => updateGrant(index, { roleIds: v })}
            />
            <DatePicker
              placeholder="生效日期"
              value={grant.validFrom ?? undefined}
              onChange={(v) => updateGrant(index, { validFrom: v ?? undefined })}
            />
            <span>至</span>
            <DatePicker
              placeholder="长期有效"
              value={grant.validTo ?? undefined}
              onChange={(v) => updateGrant(index, { validTo: v })}
              allowClear
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setGrants((prev) => prev.filter((_, i) => i !== index))}
            />
          </Space>
        ))}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setGrants((prev) => [...prev, { roleIds: [], validFrom: dayjs(), validTo: null }])}
        >
          添加校区授权
        </Button>
      </Space>
      <Text type="secondary" style={{ fontSize: 12, marginTop: 12, display: 'block' }}>
        不填结束日期表示长期有效；移除全部授权后该员工将无法登录管理端与员工端。
      </Text>
    </DraggableModal>
  );
}
